import {
  QueryClient,
  Query,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import {
  approveCampaignColumn,
  createCampaignColumn,
  deleteCampaignColumn,
  editCampaignColumnValue,
  getAgentPreflight,
  listAgentTypes,
  listCampaignColumns,
  listCampaignColumnValues,
  recomputeCampaignColumn,
  testCampaignColumnPrompt,
  updateCampaignColumn,
} from './api'
import type { CampaignColumnValue } from './types'

type ListColumnsData = Awaited<ReturnType<typeof listCampaignColumns>>
type ListColumnsQuery = Query<ListColumnsData, Error, ListColumnsData>
type ListValuesData = Awaited<ReturnType<typeof listCampaignColumnValues>>
type RefetchIntervalCallback = (
  query: ListColumnsQuery
) => number | false | undefined

export const COLUMNS_KEY = (campaignId: number) =>
  ['campaign-columns', { campaignId }] as const
export const VALUES_KEY = (columnId: number) =>
  ['campaign-column-values', { columnId }] as const

export function upsertCampaignColumnValue(
  data: ListValuesData | undefined,
  {
    columnId,
    contactId,
    value,
    computedAt = new Date().toISOString(),
  }: {
    columnId: number
    contactId: number
    value: string
    computedAt?: string
  }
): ListValuesData {
  const results = data?.results ?? []
  const existingIndex = results.findIndex((row) => row.contact === contactId)
  const existing = existingIndex >= 0 ? results[existingIndex] : undefined
  const nextValue: CampaignColumnValue = {
    id: existing?.id ?? -contactId,
    column: existing?.column ?? columnId,
    contact: contactId,
    value: { value: value.trim() },
    is_user_edited: true,
    error: null,
    version_computed: existing?.version_computed ?? null,
    computed_at: computedAt,
  }

  if (existingIndex === -1) {
    return { results: [...results, nextValue] }
  }

  return {
    results: results.map((row, index) =>
      index === existingIndex ? nextValue : row
    ),
  }
}

export function restoreCampaignColumnValue(
  data: ListValuesData | undefined,
  contactId: number,
  previous: CampaignColumnValue | undefined
): ListValuesData {
  const results = data?.results ?? []
  const existingIndex = results.findIndex((row) => row.contact === contactId)

  if (!previous) {
    return {
      results: results.filter((row) => row.contact !== contactId),
    }
  }

  if (existingIndex === -1) {
    return { results: [...results, previous] }
  }

  return {
    results: results.map((row, index) =>
      index === existingIndex ? previous : row
    ),
  }
}

export function clearDeletedCampaignColumnQueries(
  queryClient: QueryClient,
  campaignId: number,
  columnId: number
) {
  queryClient.invalidateQueries({ queryKey: COLUMNS_KEY(campaignId) })
  queryClient.removeQueries({ queryKey: VALUES_KEY(columnId) })
}

export function useListCampaignColumns(
  campaignId: number | undefined,
  options?: {
    enabled?: boolean
    // Fixed interval, false to disable, or a callback that inspects the
    // current Query and returns the next interval. Matches the signature
    // exported from @tanstack/react-query.
    refetchInterval?: number | false | RefetchIntervalCallback
  }
) {
  return useQuery({
    queryKey: COLUMNS_KEY(campaignId ?? 0),
    queryFn: () => listCampaignColumns(campaignId!),
    enabled: !!campaignId && options?.enabled !== false,
    refetchInterval: options?.refetchInterval,
  })
}

export function useListCampaignColumnValues(
  columnId: number | undefined,
  options?: { refetchInterval?: number }
) {
  return useQuery({
    queryKey: VALUES_KEY(columnId ?? 0),
    queryFn: () => listCampaignColumnValues(columnId!),
    enabled: !!columnId,
    refetchInterval: options?.refetchInterval,
  })
}

export function useCreateCampaignColumn(campaignId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: Parameters<typeof createCampaignColumn>[1]) =>
      createCampaignColumn(campaignId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COLUMNS_KEY(campaignId) })
    },
  })
}

export function useUpdateCampaignColumn(campaignId: number, columnId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: Parameters<typeof updateCampaignColumn>[1]) =>
      updateCampaignColumn(columnId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COLUMNS_KEY(campaignId) })
      queryClient.invalidateQueries({ queryKey: VALUES_KEY(columnId) })
    },
  })
}

export function useDeleteCampaignColumn(campaignId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (columnId: number) => deleteCampaignColumn(columnId),
    onSuccess: (_data, columnId) => {
      clearDeletedCampaignColumnQueries(queryClient, campaignId, columnId)
    },
  })
}

export function useRecomputeCampaignColumn(
  campaignId: number,
  columnId: number
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: Parameters<typeof recomputeCampaignColumn>[1]) =>
      recomputeCampaignColumn(columnId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COLUMNS_KEY(campaignId) })
      queryClient.invalidateQueries({ queryKey: VALUES_KEY(columnId) })
    },
  })
}

export function useEditCampaignColumnValue(columnId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      contactId,
      ...payload
    }: { contactId: number } & Parameters<typeof editCampaignColumnValue>[2]) =>
      editCampaignColumnValue(columnId, contactId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: VALUES_KEY(columnId) })
      // Editing a value bumps progress.edited on the column. The hook only
      // knows columnId, so invalidate every campaign-columns query — it's a
      // small, contact-table-scoped surface and cheaper than threading the
      // campaignId through every cell renderer.
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'campaign-columns',
      })
    },
  })
}

export function useTestCampaignColumnPrompt() {
  return useMutation({
    mutationFn: testCampaignColumnPrompt,
  })
}

export const AGENT_TYPES_KEY = (campaignId?: number) =>
  ['campaign-column-agent-types', { campaignId: campaignId ?? null }] as const
export const AGENT_PREFLIGHT_KEY = (
  campaignId: number | undefined,
  agentType: string | null | undefined,
  productIds: number[] = []
) =>
  [
    'campaign-column-agent-preflight',
    {
      campaignId: campaignId ?? null,
      agentType: agentType ?? null,
      productIds: [...productIds].sort((a, b) => a - b),
    },
  ] as const

export function useListAgentTypes(campaignId?: number) {
  return useQuery({
    queryKey: AGENT_TYPES_KEY(campaignId),
    queryFn: () => listAgentTypes(campaignId),
    staleTime: 5 * 60 * 1000, // registry is static-per-deploy + per-campaign type
  })
}

export function useAgentPreflight(
  campaignId: number | undefined,
  agentType: string | null | undefined,
  productIds: number[] = []
) {
  return useQuery({
    queryKey: AGENT_PREFLIGHT_KEY(campaignId, agentType, productIds),
    queryFn: () => getAgentPreflight(campaignId!, agentType!, productIds),
    enabled: !!campaignId && !!agentType,
    staleTime: 30 * 1000,
  })
}

export function useApproveCampaignColumn(campaignId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (columnId: number) => approveCampaignColumn(columnId),
    onSuccess: (_data, columnId) => {
      queryClient.invalidateQueries({ queryKey: COLUMNS_KEY(campaignId) })
      // Approval kicks off compute for all remaining contacts; expire cached
      // values so the UI re-fetches once new rows start landing.
      queryClient.invalidateQueries({ queryKey: VALUES_KEY(columnId) })
    },
  })
}
